import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Square, Pause, Play, Navigation, Zap, TrendingUp, Clock, AlertCircle, X, ChevronUp, ChevronDown,
  MapPin, Tent, Car, Droplets, Binoculars, AlertTriangle, UtensilsCrossed, Star, Check,
  Route, Coffee, Landmark, Plug, ShieldAlert, Waves, TreePine, Volume2, VolumeX, Flame, Download
} from 'lucide-react';
import { TrackStats, TrackingStatus } from '../hooks/useGpsTracking';
import { formatDistance, formatDuration } from '../services/trackService';
import { ElevationPoint, WaypointIcon, RouteFollowPoi, CampingSpot } from '../types';
import { getAudioEnabled, setAudioEnabled } from '../hooks/useNavigationAlerts';
import { getBearing } from '../services/geometryUtils';
import type { ContextualAlert } from '../hooks/useContextualAlerts';
import SpotPreviewOverlay from './map/SpotPreviewOverlay';

const SATELLITE_TILE = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const getSatelliteUrl = (lat: number, lon: number): string => {
  const z = 15;
  const n = Math.pow(2, z);
  const x = Math.floor(((lon + 180) / 360) * n);
  const y = Math.floor(
    ((1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) / 2) * n
  );
  return SATELLITE_TILE.replace('{z}', String(z)).replace('{y}', String(y)).replace('{x}', String(x));
};

import { openExternalNavigation } from '../services/navigationService';
import { BRAND } from '../constants/mapColors';
const openNavigation = openExternalNavigation;

const getSpotIcon = (spot: CampingSpot, size = 12) => {
  if (spot.spotType === 'rasteplass') return <Car size={size} className="text-blue-400" />;
  if (spot.spotType === 'commercial_campsite') return <Tent size={size} className="text-amber-400" />;
  if (spot.spotType === 'coastal') return <Waves size={size} className="text-cyan-400" />;
  if (spot.features?.includes('GAPAHUK')) return <TreePine size={size} className="text-emerald-400" />;
  if (spot.features?.includes('BADEPLASS')) return <Waves size={size} className="text-cyan-400" />;
  return <MapPin size={size} className="text-emerald-400" />;
};

const WAYPOINT_BUTTONS: { icon: WaypointIcon; Icon: React.FC<any>; color: string; label: string }[] = [
  { icon: 'camp',      Icon: Tent,              color: 'text-emerald-500', label: 'tracking.waypoint.camp' },
  { icon: 'parking',   Icon: Car,               color: 'text-blue-500',    label: 'tracking.waypoint.parking' },
  { icon: 'water',     Icon: Droplets,           color: 'text-cyan-500',    label: 'tracking.waypoint.water' },
  { icon: 'viewpoint', Icon: Binoculars,         color: 'text-amber-500',   label: 'tracking.waypoint.viewpoint' },
  { icon: 'danger',    Icon: AlertTriangle,      color: 'text-orange-500',  label: 'tracking.waypoint.danger' },
  { icon: 'food',      Icon: UtensilsCrossed,    color: 'text-violet-500',  label: 'tracking.waypoint.food' },
  { icon: 'star',      Icon: Star,              color: 'text-yellow-500',  label: 'tracking.waypoint.favorite' },
  { icon: 'pin',       Icon: MapPin,            color: 'text-red-500',     label: 'tracking.waypoint.pin' },
];

export interface RouteFollowDisplay {
  routeName: string;
  progressPercent: number;
  distanceFromRoute: number | null;
  isOffRoute: boolean;
  nextPois: RouteFollowPoi[];
  nearbyPoi: RouteFollowPoi | null;
  totalDistanceM: number;
  totalDurationS?: number;
  progressM: number | null;
  nearestRoutePoint?: { latitude: number; longitude: number } | null;
  userPosition?: { latitude: number; longitude: number } | null;
}

const POI_CATEGORY_META: Record<RouteFollowPoi['category'], { Icon: React.FC<any>; color: string; label: string }> = {
  wildspot:    { Icon: Tent,           color: 'text-emerald-500', label: 'tracking.poi.wildspot' },
  campsite:    { Icon: Tent,           color: 'text-blue-600',    label: 'tracking.poi.campsite' },
  rest:        { Icon: Coffee,         color: 'text-blue-400',    label: 'tracking.poi.rest' },
  attraction:  { Icon: Landmark,       color: 'text-violet-500',  label: 'tracking.poi.attraction' },
  charger:     { Icon: Plug,           color: 'text-yellow-500',  label: 'tracking.poi.charger' },
  barrier:     { Icon: ShieldAlert,    color: 'text-red-500',     label: 'tracking.poi.barrier' },
  waypoint:    { Icon: MapPin,         color: 'text-orange-500',  label: 'tracking.poi.waypoint' },
};

const POI_BORDER_COLORS: Record<RouteFollowPoi['category'], string> = {
  wildspot: 'border-l-emerald-500',
  campsite: 'border-l-blue-600',
  rest: 'border-l-blue-400',
  attraction: 'border-l-violet-500',
  charger: 'border-l-yellow-500',
  barrier: 'border-l-red-500',
  waypoint: 'border-l-orange-500',
};

interface TrackingPanelProps {
  status: TrackingStatus;
  stats: TrackStats;
  pointCount: number;
  onPause: () => void;
  onResume: () => void;
  onStop: (name: string) => void;
  onDiscard: () => void;
  gpsError: string | null;
  elevationProfile?: ElevationPoint[];
  onAddWaypoint?: (icon: WaypointIcon) => void;
  waypointCount?: number;
  routeFollow?: RouteFollowDisplay;
  onDownloadRoute?: () => void;
  nearestSpot?: CampingSpot;
  nearestSpotDistanceM?: number;
  onSelectSpot?: (id: string) => void;
  onShowSpotDetails?: (id: string) => void;
  sidebarWidthPx?: number;
  isDesktop?: boolean;
  hideNearestSpotCard?: boolean;
  contextualAlerts?: ContextualAlert[];
  forcePeek?: boolean;
  onSnapChange?: (snap: 'mini' | 'peek' | 'half' | 'full') => void;
  requestedSnap?: 'mini' | 'peek' | 'half' | 'full' | null;
}

const MiniElevationChart: React.FC<{ profile: ElevationPoint[] }> = ({ profile }) => {
  const { t } = useTranslation();
  if (profile.length < 2) return null;
  const W = 300; const H = 48;
  const maxDist = profile[profile.length - 1].distance;
  const eles = profile.map(p => p.elevation);
  const minE = Math.min(...eles); const maxE = Math.max(...eles);
  const range = maxE - minE || 1;
  const pts = profile.map(p => {
    const x = (p.distance / maxDist) * W;
    const y = H - ((p.elevation - minE) / range) * (H - 8) - 4;
    return `${x},${y}`;
  }).join(' ');
  const fillD = `M ${pts} L ${W},${H} L 0,${H} Z`;
  const gain = eles.reduce((g, e, i) => i > 0 && e > eles[i - 1] ? g + (e - eles[i - 1]) : g, 0);
  return (
    <div className="px-4 pb-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-3xs font-semibold text-stone-400 uppercase tracking-wide flex items-center gap-1">
          <TrendingUp size={9} /> {t("auto.trackingpanel.hoydeprofil_live", "Høydeprofil (live)")}
        </span>
        <span className="text-2xs font-bold text-emerald-500">+{Math.round(gain)}m</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-12 overflow-visible" preserveAspectRatio="none">
        <defs>
          <linearGradient id="liveEleGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={BRAND} stopOpacity="0.35" />
            <stop offset="100%" stopColor={BRAND} stopOpacity="0.05" />
          </linearGradient>
        </defs>
        <path d={fillD} fill="url(#liveEleGrad)" />
        <polyline points={pts} fill="none" stroke={BRAND} strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
      </svg>
      <div className="flex justify-between text-4xs text-[var(--text-muted)] font-bold mt-0.5">
        <span>{Math.round(minE)}m</span>
        <span>{Math.round(maxE)}m</span>
      </div>
    </div>
  );
};

const TrackingPanel: React.FC<TrackingPanelProps> = ({
  status,
  stats,
  pointCount,
  onPause,
  onResume,
  onStop,
  onDiscard,
  gpsError,
  elevationProfile,
  onAddWaypoint,
  waypointCount = 0,
  routeFollow,
  onDownloadRoute,
  nearestSpot,
  nearestSpotDistanceM,
  onSelectSpot,
  onShowSpotDetails,
  sidebarWidthPx = 0,
  isDesktop = false,
  hideNearestSpotCard = false,
  contextualAlerts = [],
  forcePeek = false,
  onSnapChange,
  requestedSnap = null,
}) => {
  const { t } = useTranslation();
  type SnapPoint = 'mini' | 'peek' | 'half' | 'full';
  const [snapPoint, setSnapPointRaw] = useState<SnapPoint>(forcePeek ? 'peek' : 'half');
  const setSnapPoint = (sp: SnapPoint) => {
    setSnapPointRaw(sp);
    onSnapChange?.(sp);
  };
  const collapsed = snapPoint === 'peek'; // compat
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [showElevation, setShowElevation] = useState(false);
  const [showWaypoints, setShowWaypoints] = useState(false);
  const [justAdded, setJustAdded] = useState<WaypointIcon | null>(null);
  const dragStartY = useRef<number | null>(null);
  const dragStartSnap = useRef<SnapPoint>('half');
  const [nearbyDismissed, setNearbyDismissed] = useState<string | null>(null);
  const [audioOn, setAudioOn] = useState(getAudioEnabled);
  const [showSpotPreview, setShowSpotPreview] = useState(false);
  const nearbyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [trackName, setTrackName] = useState(() => {
    const d = new Date();
    return `Tur ${d.getDate()}. ${d.toLocaleString('nb-NO', { month: 'long' })} ${d.getFullYear()}`;
  });

  // Enforce peek when tablet follow panel takes over
  useEffect(() => {
    if (forcePeek && snapPoint !== 'peek' && snapPoint !== 'mini') setSnapPoint('peek');
  }, [forcePeek]);

  // Respond to external snap requests (e.g. recorder toggle button)
  useEffect(() => {
    if (requestedSnap && requestedSnap !== snapPoint) {
      setSnapPoint(requestedSnap);
    }
  }, [requestedSnap]);

  // Auto-dismiss nearby POI alert etter 10s
  useEffect(() => {
    if (routeFollow?.nearbyPoi && routeFollow.nearbyPoi.id !== nearbyDismissed) {
      if (nearbyTimerRef.current) clearTimeout(nearbyTimerRef.current);
      nearbyTimerRef.current = setTimeout(() => {
        setNearbyDismissed(routeFollow.nearbyPoi?.id ?? null);
      }, 10000);
    }
    return () => { if (nearbyTimerRef.current) clearTimeout(nearbyTimerRef.current); };
  }, [routeFollow?.nearbyPoi?.id]);

  // ── Drag gesture handlers for snap points ──
  const snapOrder: SnapPoint[] = ['mini', 'peek', 'half', 'full'];
  const handleTouchStart = (e: React.TouchEvent) => {
    if (forcePeek) return;
    dragStartY.current = e.touches[0].clientY;
    dragStartSnap.current = snapPoint;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (forcePeek || dragStartY.current === null) return;
    const deltaY = e.changedTouches[0].clientY - dragStartY.current;
    const threshold = 40;
    const currentIdx = snapOrder.indexOf(dragStartSnap.current);
    if (deltaY < -threshold && currentIdx < snapOrder.length - 1) {
      // Swipe up → expand
      setSnapPoint(snapOrder[currentIdx + 1]);
    } else if (deltaY > threshold && currentIdx > 0) {
      // Swipe down → collapse
      setSnapPoint(snapOrder[currentIdx - 1]);
    }
    dragStartY.current = null;
  };

  const cycleSnap = (direction: 'up' | 'down') => {
    if (forcePeek) return;
    const idx = snapOrder.indexOf(snapPoint);
    if (direction === 'up' && idx < snapOrder.length - 1) setSnapPoint(snapOrder[idx + 1]);
    if (direction === 'down' && idx > 0) setSnapPoint(snapOrder[idx - 1]);
  };

  const hasElevation = stats.elevationGain > 0 || stats.elevationLoss > 0;

  const speedKmh = stats.currentSpeed !== null
    ? (stats.currentSpeed * 3.6).toFixed(1)
    : null;

  const handleAddWaypoint = (icon: WaypointIcon) => {
    onAddWaypoint?.(icon);
    setJustAdded(icon);
    setTimeout(() => setJustAdded(null), 1200);
  };

  const nearestSpotPhoto = nearestSpot?.photoUrls?.[0] || nearestSpot?.auto_image_url;
  const nearestSpotImg = nearestSpot
    ? nearestSpotPhoto || getSatelliteUrl(nearestSpot.location.latitude, nearestSpot.location.longitude)
    : null;
  const nearestDistLabel = nearestSpotDistanceM != null
    ? nearestSpotDistanceM < 1000 ? `${Math.round(nearestSpotDistanceM)}m` : `${(nearestSpotDistanceM / 1000).toFixed(1)}km`
    : null;

  // Shared nearest-spot card renderer
  const renderNearestSpotCard = () => {
    if (!nearestSpot) return null;
    return (
      <div
        className="mx-4 mb-2 flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-2.5 py-2 cursor-pointer hover:bg-emerald-100 transition-colors"
        onClick={() => onSelectSpot?.(nearestSpot.id)}
      >
        <div className="w-11 h-11 rounded-lg overflow-hidden bg-stone-200 shrink-0">
          <img src={nearestSpotImg!} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            {getSpotIcon(nearestSpot, 11)}
            <span className="text-tiny font-bold text-stone-800 truncate">
              {nearestSpot.name || 'Ukjent plass'}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {nearestSpot.score > 0 && (
              <span className="flex items-center gap-0.5 text-3xs font-bold text-amber-600">
                <Star size={8} fill="currentColor" /> {nearestSpot.score.toFixed(1)}
              </span>
            )}
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{ backgroundColor: nearestSpot.suitabilityColor || '#22c55e' }}
            />
            {nearestDistLabel && (
              <span className="text-3xs font-semibold text-stone-500">{nearestDistLabel}</span>
            )}
          </div>
        </div>
        <button
          className="shrink-0 px-2.5 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-3xs font-bold uppercase tracking-wider transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            setShowSpotPreview(true);
          }}
        >
          {t("auto.trackingpanel.vis", "Vis")}
        </button>
        {onShowSpotDetails && (
          <button
            className="shrink-0 px-2.5 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-3xs font-bold uppercase tracking-wider transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onShowSpotDetails(nearestSpot.id);
            }}
          >
            {t("auto.trackingpanel.detaljer", "Detaljer")}
          </button>
        )}
        <button
          className="shrink-0 flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-500 hover:bg-emerald-600 transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            openNavigation(nearestSpot.location.latitude, nearestSpot.location.longitude);
          }}
        >
          <Navigation size={13} className="text-white" />
        </button>
      </div>
    );
  };

  return (
    <>
      {/* ── Minimert pille — vises i stedet for panelet ── */}
      {snapPoint === 'mini' && (
        <div className="absolute bottom-0 sm:bottom-2 z-sticky pointer-events-auto" style={isDesktop && sidebarWidthPx ? { left: `${sidebarWidthPx}px`, right: 0 } : { right: 0 }}>
          <div className="mx-3 mb-3 flex justify-end">
            <button
              onClick={() => setSnapPoint('half')}
              className={`flex items-center gap-2.5 px-4 py-2.5 rounded-2xl shadow-xl backdrop-blur-xl transition-all active:scale-95 ${
                routeFollow?.isOffRoute
                  ? 'bg-red-500/90 text-white'
                  : 'bg-white/90 text-stone-700 border border-stone-200'
              }`}
            >
              <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                status === 'recording' ? 'bg-red-500 animate-pulse' : 'bg-amber-400'
              } ${routeFollow?.isOffRoute ? 'bg-white animate-pulse' : ''}`} />
              <span className="text-[12px] font-black">
                {routeFollow && routeFollow.totalDistanceM > 0 && routeFollow.progressM != null
                  ? formatDistance(Math.max(0, routeFollow.totalDistanceM - routeFollow.progressM))
                  : formatDistance(stats.distance)}
              </span>
              <span className="text-2xs opacity-70 font-bold">·</span>
              <span className="text-[12px] font-bold opacity-80">
                {routeFollow?.totalDurationS && routeFollow.totalDurationS > 0 && routeFollow.progressPercent > 0
                  ? formatDuration(Math.max(0, Math.round(routeFollow.totalDurationS * (1 - routeFollow.progressPercent / 100))))
                  : routeFollow?.totalDurationS && routeFollow.totalDurationS > 0
                    ? formatDuration(routeFollow.totalDurationS)
                    : formatDuration(stats.duration)}
              </span>
              {speedKmh && (
                <>
                  <span className="text-2xs opacity-70 font-bold">·</span>
                  <span className="text-tiny font-bold opacity-70">
                    {speedKmh} km/t
                  </span>
                </>
              )}
              <ChevronUp size={14} className="opacity-50 ml-0.5" />
            </button>
          </div>
        </div>
      )}

      {/* Bunnpanel under sporing */}
      <div className={`absolute bottom-0 sm:bottom-2 z-sticky pointer-events-auto ${snapPoint === 'mini' ? 'hidden' : ''}`} style={{ left: isDesktop && sidebarWidthPx ? `${sidebarWidthPx}px` : 0, right: 0 }}>
        <div
          className={`mx-2 sm:mx-auto mb-2 sm:max-w-lg rounded-3xl shadow-2xl overflow-hidden transition-all duration-300 ${
            routeFollow?.isOffRoute
              ? 'bg-red-50 border-2 border-red-400 ring-2 ring-red-400/30'
              : 'bg-white border border-stone-200'
          }`}
          style={{ maxHeight: snapPoint === 'peek' ? '80px' : snapPoint === 'half' ? '40dvh' : '85dvh' }}
        >
          {/* ── Drag handle ── */}
          <div
            className={`flex justify-center pt-2 pb-1 ${forcePeek ? '' : 'cursor-pointer'} touch-none select-none`}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onClick={() => !forcePeek && cycleSnap(snapPoint === 'peek' || snapPoint === 'mini' ? 'up' : 'down')}
          >
            <div className="w-10 h-1 rounded-full bg-stone-300" />
          </div>

          {/* ── PEEK: Stats bar + controls (always visible) ── */}
          <div className="flex items-center gap-2 px-3 pb-2">
            <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${status === 'recording' ? 'bg-red-500 animate-pulse' : 'bg-amber-400'}`} />
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <span className="flex items-center gap-1">
                <Navigation size={10} className="text-emerald-500" />
                <span className="text-[13px] font-black text-stone-800">{formatDistance(stats.distance)}</span>
              </span>
              <span className="flex items-center gap-1">
                <Clock size={10} className="text-stone-400" />
                <span className="text-[13px] font-bold text-stone-600">{formatDuration(stats.duration)}</span>
              </span>
              {hasElevation && (
                <span className="flex items-center gap-1">
                  <TrendingUp size={10} className="text-stone-400" />
                  <span className="text-tiny font-semibold text-stone-500">+{stats.elevationGain}m</span>
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {status === 'recording' ? (
                <button onClick={onPause} className="min-w-[40px] min-h-[40px] flex items-center justify-center rounded-xl bg-stone-100 hover:bg-stone-200 transition-all active:scale-95">
                  <Pause size={16} className="text-stone-500" />
                </button>
              ) : (
                <button onClick={onResume} className="min-w-[40px] min-h-[40px] flex items-center justify-center rounded-xl bg-emerald-100 hover:bg-emerald-200 transition-all active:scale-95">
                  <Play size={16} className="text-emerald-600" />
                </button>
              )}
              <button onClick={() => setShowStopDialog(true)} className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl bg-red-500 hover:bg-red-600 transition-all active:scale-95">
                <Square size={16} className="text-white" />
              </button>
              <button onClick={() => cycleSnap(snapPoint === 'full' ? 'down' : 'up')} className="min-w-[36px] min-h-[36px] flex items-center justify-center rounded-xl hover:bg-stone-100 transition-all">
                {snapPoint === 'full' ? <ChevronDown size={16} className="text-stone-400" /> : <ChevronUp size={16} className="text-stone-400" />}
              </button>
            </div>
          </div>

          {/* GPS error — always visible regardless of snap point */}
          {gpsError && (
            <div className="mx-3 mb-2 flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
              <AlertCircle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-tiny text-red-600 break-words">{gpsError}</p>
            </div>
          )}

          {/* ── HALF: Route context + nearest spot + contextual alerts (visible at half + full) ── */}
          {(snapPoint === 'half' || snapPoint === 'full') && (
            <div className="overflow-y-auto" style={{ maxHeight: snapPoint === 'half' ? 'calc(40dvh - 80px)' : undefined, overscrollBehavior: 'contain' }}>
              {/* Rutekontekst mini (half view) */}
              {routeFollow && snapPoint === 'half' && (
                <div className="px-3 pb-1.5">
                  <div className="flex items-center gap-2">
                    {routeFollow.isOffRoute && (
                      <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
                    )}
                    <div className="flex-1 h-1 bg-stone-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full transition-all duration-700" style={{ width: `${Math.min(100, routeFollow.progressPercent)}%` }} />
                    </div>
                    {routeFollow.nextPois[0] && (() => {
                      const distAhead = (routeFollow.progressM !== null)
                        ? routeFollow.nextPois[0].distanceAlongRoute - routeFollow.progressM
                        : routeFollow.nextPois[0].distanceAlongRoute;
                      return (
                        <span className="text-3xs font-bold text-stone-500 shrink-0 truncate max-w-[100px]">
                          {routeFollow.nextPois[0].name} {formatDistance(distAhead)}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Nearest spot */}
              {!hideNearestSpotCard && renderNearestSpotCard()}

              {/* Contextual alerts */}
              {contextualAlerts.length > 0 && (
                <div className="mx-4 mb-2 flex flex-wrap gap-1.5">
                  {contextualAlerts.map(alert => {
                    const IconComp = alert.iconName === 'Plug' ? Plug
                      : alert.iconName === 'Waves' ? Waves
                      : alert.iconName === 'ShieldAlert' ? ShieldAlert
                      : Flame;
                    return (
                      <div
                        key={alert.id}
                        className={`flex items-center gap-1.5 ${alert.bgColor} border ${alert.borderColor} rounded-xl px-2.5 py-1.5`}
                      >
                        <IconComp size={13} className={alert.color} />
                        <div className="min-w-0">
                          <span className={`text-2xs font-bold ${alert.color} block leading-tight`}>
                            {alert.title}
                          </span>
                          {alert.subtitle && (
                            <span className="text-4xs text-stone-500 block leading-tight truncate max-w-[120px]">
                              {alert.subtitle}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Off-route alert with direction arrow */}
              {routeFollow?.isOffRoute && routeFollow.distanceFromRoute !== null && (() => {
                const bearingToRoute = routeFollow.userPosition && routeFollow.nearestRoutePoint
                  ? getBearing(
                      routeFollow.userPosition.latitude, routeFollow.userPosition.longitude,
                      routeFollow.nearestRoutePoint.latitude, routeFollow.nearestRoutePoint.longitude
                    )
                  : null;
                return (
                  <div className="mx-4 mb-2 flex items-center gap-2 bg-red-100 border border-red-300 rounded-xl px-3 py-2.5 animate-pulse">
                    {bearingToRoute !== null ? (
                      <Navigation
                        size={18}
                        className="text-red-600 flex-shrink-0 transition-transform duration-500"
                        style={{ transform: `rotate(${bearingToRoute}deg)` }}
                      />
                    ) : (
                      <AlertTriangle size={16} className="text-red-500 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <span className="text-[12px] text-red-700 font-black block">
                        {Math.round(routeFollow.distanceFromRoute)}m fra ruten
                      </span>
                      <span className="text-3xs text-red-500 font-medium">
                        {t("auto.trackingpanel.folg_pilen_tilbake", "Følg pilen tilbake")}
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── FULL: Everything else (only at full snap) ── */}
          {snapPoint === 'full' && (
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(85dvh - 200px)', overscrollBehavior: 'contain' }}>
              {/* Expanded stats grid */}
              <div className="grid grid-cols-3 gap-0 px-4 pb-3 pt-1">
                <div className="flex flex-col items-center">
                  <span className="text-3xs text-stone-400 font-medium uppercase tracking-wide mb-0.5">{t("auto.trackingpanel.distanse", "Distanse")}</span>
                  <span className="text-xl font-black text-stone-800 leading-none">
                    {formatDistance(stats.distance)}
                  </span>
                </div>
                <div className="flex flex-col items-center border-x border-stone-100">
                  <span className="text-3xs text-stone-400 font-medium uppercase tracking-wide mb-0.5">{t("auto.trackingpanel.tid", "Tid")}</span>
                  <span className="text-xl font-black text-stone-800 leading-none">
                    {formatDuration(stats.duration)}
                  </span>
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-3xs text-stone-400 font-medium uppercase tracking-wide mb-0.5">{t("auto.trackingpanel.stigning", "Stigning")}</span>
                  <span className="text-xl font-black text-stone-800 leading-none">
                    {hasElevation ? `+${stats.elevationGain}m` : '—'}
                  </span>
                </div>
              </div>

              {/* Audio toggle + full controls row */}
              <div className="flex items-center justify-between px-4 pb-2">
                <button
                  onClick={() => { const next = !audioOn; setAudioOn(next); setAudioEnabled(next); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-2xs font-bold transition-all active:scale-95 ${
                    audioOn ? 'bg-emerald-50 text-emerald-600' : 'bg-stone-100 text-stone-400'
                  }`}
                >
                  {audioOn ? <Volume2 size={12} /> : <VolumeX size={12} />}
                  {audioOn ? 'Lyd på' : 'Lyd av'}
                </button>
                <div className="flex items-center gap-1.5">
                  {status === 'recording' ? (
                    <button onClick={onPause} className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-stone-100 hover:bg-stone-200 text-2xs font-bold text-[var(--text-muted)] active:scale-95">
                      <Pause size={12} /> {t("auto.trackingpanel.pause", "Pause")}
                    </button>
                  ) : (
                    <button onClick={onResume} className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-emerald-100 hover:bg-emerald-200 text-2xs font-bold text-emerald-700 active:scale-95">
                      <Play size={12} /> {t("auto.trackingpanel.fortsett", "Fortsett")}
                    </button>
                  )}
                  <button onClick={() => setShowStopDialog(true)} className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-red-500 hover:bg-red-600 text-2xs font-bold text-white active:scale-95">
                    <Square size={12} /> {t("auto.trackingpanel.stopp", "Stopp")}
                  </button>
                </div>
              </div>

              {/* Full route context */}
              {routeFollow && (
                <div className="border-t border-stone-100 px-4 py-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-3xs font-semibold text-stone-400 uppercase tracking-wide flex items-center gap-1">
                      <Route size={9} /> {routeFollow.routeName}
                    </span>
                    <span className="text-2xs font-bold text-stone-600">
                      {formatDistance(routeFollow.progressM ?? 0)} / {formatDistance(routeFollow.totalDistanceM)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full transition-all duration-700 ease-out"
                        style={{ width: `${Math.min(100, routeFollow.progressPercent)}%` }}
                      />
                    </div>
                    {onDownloadRoute && (
                      <button
                        onClick={onDownloadRoute}
                        className="shrink-0 w-7 h-7 rounded-lg bg-blue-50 border border-blue-200 text-blue-500 flex items-center justify-center active:scale-90 transition-all"
                        title={t("auto.trackingpanel.title.last_ned_rute", "Last ned rute")}
                      >
                        <Download size={12} />
                      </button>
                    )}
                  </div>

                  {routeFollow.nearbyPoi && routeFollow.nearbyPoi.id !== nearbyDismissed && (() => {
                    const meta = POI_CATEGORY_META[routeFollow.nearbyPoi!.category];
                    return (
                      <div className="mt-2 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 animate-pulse">
                        <meta.Icon size={14} className={meta.color} />
                        <span className="text-tiny text-amber-700 font-bold flex-1">
                          {routeFollow.nearbyPoi!.name} {t('tracking.nearby')}
                        </span>
                      </div>
                    );
                  })()}

                  {routeFollow.nextPois.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {routeFollow.nextPois.slice(0, 3).map(poi => {
                        const meta = POI_CATEGORY_META[poi.category];
                        const distAhead = (routeFollow.progressM !== null)
                          ? poi.distanceAlongRoute - routeFollow.progressM
                          : poi.distanceAlongRoute;
                        return (
                          <div
                            key={poi.id}
                            className={`flex items-center gap-2 bg-stone-50 rounded-lg px-2.5 py-1.5 border-l-2 ${POI_BORDER_COLORS[poi.category]}`}
                          >
                            <meta.Icon size={12} className={meta.color} />
                            <span className="text-2xs font-bold text-stone-700 flex-1 truncate">{poi.name}</span>
                            <span className="text-3xs text-[var(--text-muted)] font-medium whitespace-nowrap">
                              {t('tracking.in_distance', { distance: formatDistance(distAhead) })}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Waypoint buttons */}
              {onAddWaypoint && (
                <div className="border-t border-stone-100">
                  <button
                    onClick={() => setShowWaypoints(v => !v)}
                    className="w-full flex items-center justify-between px-4 py-1.5 text-2xs font-semibold text-stone-400 hover:text-stone-500 transition-colors uppercase tracking-wide"
                  >
                    <span className="flex items-center gap-1">
                      <MapPin size={10} />
                      Legg til waypoint
                      {waypointCount > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-600 text-4xs font-black">
                          {waypointCount}
                        </span>
                      )}
                    </span>
                    {showWaypoints ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                  {showWaypoints && (
                    <div className="px-4 pb-3 flex flex-wrap gap-2 justify-center">
                      {WAYPOINT_BUTTONS.map(({ icon, Icon, color, label }) => (
                        <button
                          key={icon}
                          onClick={() => handleAddWaypoint(icon)}
                          className={`flex flex-col items-center gap-0.5 min-w-[44px] min-h-[44px] py-2 px-1.5 rounded-xl transition-all active:scale-90 ${
                            justAdded === icon
                              ? 'bg-emerald-100 scale-110'
                              : 'bg-stone-50 hover:bg-stone-100'
                          }`}
                          title={t(label)}
                        >
                          {justAdded === icon ? (
                            <Check size={14} className="text-emerald-500" />
                          ) : (
                            <Icon size={14} className={color} />
                          )}
                          <span className="text-[7px] font-bold text-[var(--text-muted)] leading-none">{t(label)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Elevation profile */}
              {elevationProfile && elevationProfile.length >= 2 && (
                <>
                  <div className="border-t border-stone-100">
                    <button
                      onClick={() => setShowElevation(v => !v)}
                      className="w-full flex items-center justify-between px-4 py-1.5 text-2xs font-semibold text-stone-400 hover:text-stone-500 transition-colors uppercase tracking-wide"
                    >
                      <span className="flex items-center gap-1"><TrendingUp size={10} /> {t("auto.trackingpanel.hoydeprofil", "Høydeprofil")}</span>
                      {showElevation ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                  </div>
                  {showElevation && <MiniElevationChart profile={elevationProfile} />}
                </>
              )}

              {/* GPS status */}
              <div className="flex items-center justify-between px-4 pb-3 border-t border-stone-100 pt-2">
                <div className="flex items-center gap-1 text-2xs text-[var(--text-secondary)]">
                  <div className={`w-1.5 h-1.5 rounded-full ${
                    stats.currentAccuracy !== null && stats.currentAccuracy <= 20 ? 'bg-emerald-400' :
                    stats.currentAccuracy !== null && stats.currentAccuracy <= 50 ? 'bg-amber-400' :
                    'bg-red-400'
                  }`} />
                  GPS: {stats.currentAccuracy !== null ? `${Math.round(stats.currentAccuracy)}m nøyaktighet` : 'Søker...'}
                </div>
                <div className="flex items-center gap-3 text-2xs text-[var(--text-secondary)]">
                  {speedKmh && (
                    <span className="flex items-center gap-1">
                      <Zap size={10} className="text-[var(--text-secondary)]" />
                      {speedKmh} km/t
                    </span>
                  )}
                  <span>{pointCount} punkt</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stopp-dialog */}
      {showStopDialog && (
        <div className="absolute inset-0 z-[4500] flex items-end sm:items-center justify-center pointer-events-auto bg-[var(--bg-input)] backdrop-blur-sm">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full sm:w-80 sm:max-w-[calc(100vw-2rem)] sm:mx-4 max-h-[85dvh] overflow-y-auto animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200" style={{ overscrollBehavior: 'contain', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-black text-stone-800">{t("auto.trackingpanel.lagre_tur", "Lagre tur")}</h3>
                <button
                  onClick={() => setShowStopDialog(false)}
                  className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full hover:bg-stone-100 text-[var(--text-secondary)]"
                >
                  <X size={15} />
                </button>
              </div>

              {/* Oppsummering */}
              <div className="grid grid-cols-3 gap-2 mb-4 bg-stone-50 rounded-2xl p-3">
                <div className="text-center">
                  <div className="text-tiny text-[var(--text-secondary)] mb-0.5">{t("auto.trackingpanel.distanse", "Distanse")}</div>
                  <div className="text-sm font-black text-stone-800">{formatDistance(stats.distance)}</div>
                </div>
                <div className="text-center border-x border-stone-200">
                  <div className="text-tiny text-[var(--text-secondary)] mb-0.5">{t("auto.trackingpanel.tid", "Tid")}</div>
                  <div className="text-sm font-black text-stone-800">{formatDuration(stats.duration)}</div>
                </div>
                <div className="text-center">
                  <div className="text-tiny text-[var(--text-secondary)] mb-0.5">{t("auto.trackingpanel.stigning", "Stigning")}</div>
                  <div className="text-sm font-black text-stone-800">
                    {hasElevation ? `+${stats.elevationGain}m` : '—'}
                  </div>
                </div>
              </div>

              {/* Waypoint-oppsummering */}
              {waypointCount > 0 && (
                <div className="flex items-center gap-2 mb-3 px-1">
                  <MapPin size={12} className="text-emerald-500" />
                  <span className="text-tiny text-[var(--text-secondary)]">{waypointCount} waypoints lagt til</span>
                </div>
              )}

              {/* Navnefelt */}
              <label className="block mb-4">
                <span className="text-tiny font-bold text-[var(--text-muted)] uppercase tracking-wide block mb-1.5">
                  {t("auto.trackingpanel.navn_pa_turen", "Navn på turen")}
                </span>
                <input
                  type="text"
                  value={trackName}
                  onChange={(e) => setTrackName(e.target.value)}
                  className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm text-stone-800 bg-stone-50 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  autoFocus
                />
              </label>

              {/* Knapper */}
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowStopDialog(false);
                    onDiscard();
                  }}
                  className="flex-1 py-3 min-h-[44px] rounded-2xl border border-stone-200 text-[var(--text-muted)] text-sm font-bold hover:bg-stone-50 transition-all"
                >
                  {t("auto.trackingpanel.forkast", "Forkast")}
                </button>
                <button
                  onClick={() => {
                    setShowStopDialog(false);
                    onStop(trackName.trim() || 'Min tur');
                  }}
                  disabled={pointCount < 2}
                  className="flex-1 py-3 min-h-[44px] rounded-2xl bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {t("auto.trackingpanel.lagre", "Lagre")}
                </button>
              </div>

              {pointCount < 2 && (
                <p className="text-2xs text-[var(--text-secondary)] text-center mt-2">
                  {t("auto.trackingpanel.beveg_deg_litt_for_a_registrer", "Beveg deg litt for å registrere GPS-punkter.")}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Spot preview overlay */}
      {showSpotPreview && nearestSpot && (
        <SpotPreviewOverlay
          spot={nearestSpot}
          distanceM={nearestSpotDistanceM}
          onClose={() => setShowSpotPreview(false)}
          onDetails={(id) => { setShowSpotPreview(false); onShowSpotDetails?.(id); }}
          userPosition={routeFollow?.userPosition}
        />
      )}
    </>
  );
};

export default TrackingPanel;
